import _ from 'lodash'
import { useEffect, useState, useCallback, useRef } from 'react'
import { vehicleApi } from '@/api'
import { Vehicle } from '@cruz-com/lookup'
import { Section } from './vehicleFilterSection'
import { Checkbox, Text } from '@/components'
import { MakeModelListBox } from './vehicleMakeModelListBox'

type mmQuery = {
  make?: string
  model?: string | null
  trim?: string | null
}

type Props = {
  type?: string
  className?: string
  name: string
  title: string
  otherQuery: Vehicle.Query
  mm: string
  makeOptions: any
  onChange: (val: any) => void
} & (React.ComponentPropsWithoutRef<'select'>)

const ignoreFilters = ['mm1', 'mm2', 'mm3', 'make', 'model', 'trim']
const mmStyle = 'text-sm/10 w-[60px] font-medium'

export function MakeMode({ className, name, title, type, otherQuery, mm, makeOptions, onChange }: Props) {
  function getQueryFromMmt() {
    if (!mm) {
      return { make: '' }
    }
    const y = _.split(mm, ',')
    return { make: y[0], model: y[1] }
  }

  const sectionRef = useRef<null | HTMLDivElement>(null)
  const [query, setQuery] = useState<mmQuery>(getQueryFromMmt())
  const [isMakeChanged, setIsMakeChanged] = useState(false) // used for scroll into view
  const [modelOption, setModelOption] = useState<any>(null)
  const [modelKeyword, setModelKeyword] = useState('')

  // mm changed
  useEffect(() => {
    setQuery(getQueryFromMmt())
  }, [mm])

  // update parent
  useEffect(() => {
    if (query?.make) {
      onChange(_.compact([query.make, query?.model]).join(','))
    } else {
      onChange(null)
    }
  }, [query])

  // make changed
  function makeChanged(val: string | any) {
    setQuery({ make: val, model: null })
  }

  useEffect(() => {
    setModelOption(null)
    setIsMakeChanged(true)
    if (query?.make) {
      getModelOption()
    }
  }, [query?.make])

  // models
  async function getModelOption() {
    const d = await vehicleApi.getModelOption({ ..._.omit(otherQuery, ignoreFilters), make: query.make }, type)
    const res = _.concat(d?.popular, d?.other)
    setModelOption(res)
  }

  useEffect(() => {
    if (!_.isEmpty(modelOption) && isMakeChanged) {
      sectionRef?.current?.scrollIntoView({ behavior: 'smooth' })
      setIsMakeChanged(false)
    }
  }, [modelOption])

  function modelSelected(model: string, val: boolean) {
    const x: string[] = query?.model ? _.split(query.model as string, '|') : []
    const y = val ? _.concat(x, [model]) : _.without(x, model)
    setQuery({ ...query, model: _.join(y, '|') })
  }

  const filteredModels = useCallback(() => {
    if (_.isEmpty(modelKeyword)) { return modelOption }
    return _.filter(modelOption, (x: string) => x.toLowerCase().includes(modelKeyword.toLowerCase()))
  }, [modelOption, modelKeyword])

  // models
  const Models = () => {
    if (_.isEmpty(modelOption)) {
      return null
    }
    return (
      <div className='flex items-start w-full'>
        <div className={mmStyle}>Models:</div>
        <div className='flex-1'>
          <div className='mb-3 w-full md:w-[200px]'>
            <Text placeholder='Search Model' compact={true} name='mf' type='text' value={modelKeyword} onChange={e => setModelKeyword(e.target.value || '')} />
          </div>
          <ul className='grid grid-cols-1 md:grid-cols-2 gap-x-4 mt-1'>
            {
              _.map(filteredModels(), (name, idx) => {
                const x = _.split(query?.model, '|')
                return (
                  <li key={idx}>
                    <Checkbox name={name} label={name} checked={x.includes(name)} onChange={val => modelSelected(name, val)} />
                  </li>)
              })
            }
          </ul>
        </div>
      </div>
    )
  }


  return (
    <div ref={sectionRef} className='w-full'>
      <Section name={name} title={title} hasValue={!_.isEmpty(query?.make)} className={className}>
        <div className='flex flex-col gap-y-3 w-full' ref={sectionRef}>
          <div className='flex items-start' ref={sectionRef}>
            <div className={mmStyle}>Make:</div>
            <div className='w-[200px]'>
              <MakeModelListBox value={query?.make || 'Any'} option={makeOptions} onChange={val => makeChanged(val)} />
            </div>
          </div>
          {Models()}
        </div>
      </Section>
    </div>
  )
}